import { KeyedArray } from "@repo/models";
import { createContext, useContext, useMemo, useState, useTransition } from "react";

import type { MapConfiguration } from "../../../models/mapConfiguration";

import { useMapConfigurations } from "../../../api/mapConfigurationController";

export const NEW_MAP_ID = 0;

interface MapConfigContextValue {
	mapConfigId: number | null;
	setMapConfigId: (id: number | null) => void;
	configs: KeyedArray<MapConfiguration>;
	currentConfig: MapConfiguration | undefined;
	isTransitioning: boolean;
	startTransition: (callback: () => void) => void;
}

const MapConfigContext = createContext<MapConfigContextValue | null>(null);

export function useMapConfig() {
	const context = useContext(MapConfigContext);
	if (!context) throw new Error("useMapConfig must be used within a MapConfigProvider");
	return context;
}

export function MapConfigProvider({ children }: { children: React.ReactNode }) {
	const [mapConfigId, setMapConfigId] = useState<number | null>(null);
	const [isTransitioning, startTransition] = useTransition();

	const { data } = useMapConfigurations();
	const configs = useMemo(() => new KeyedArray<MapConfiguration>(data ?? [], (c) => c.id), [data]);
	const currentConfig = configs.get(mapConfigId);

	return (
		<MapConfigContext.Provider
			value={{ mapConfigId, setMapConfigId, configs, currentConfig, isTransitioning, startTransition }}
		>
			{children}
		</MapConfigContext.Provider>
	);
}
